import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getGatewayUpdateCustomerDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.form-default-values';
import { GatewayUpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.model';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { UpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/customers/update-customer.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdateCustomerMutation } from '@service/cpms-bo-customer.service';

import {
  UpdateCustomerContextProvider,
  useCustomerContext,
} from '../customer.context';
import { CustomerUpdateFormController } from './customer-update-form/customer-update-form.controller';

interface CustomerEditControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const CustomerUpdateController = ({
  onCancel,
  onSuccess,
}: CustomerEditControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateCustomerMutation] = useCpmsBoUpdateCustomerMutation();
  const customer = useCustomerContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    }
  > = (errors) => {
    Logger.error(errors);
    toast.error(t('$.customer-update.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    }
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.customer) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    const updateCustomer = {
      customerId: fieldValues.customerId,
      customer: sanitizedObject.customer,
    };

    updateCustomerMutation(updateCustomer)
      .unwrap()
      .then(() => {
        toast.success(t('$.customer-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.customer-update.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getGatewayUpdateCustomerDefaultValues(customer);

  return (
    <UpdateCustomerContextProvider data={data}>
      <CustomerUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateCustomerContextProvider>
  );
};
